import React, {useContext, useEffect, useRef, useState} from "react";

import {useLoadedAssetsDispatch, useLoadedAssetsState} from "../../contexts/LoadedAssetsContext";
import {useAggsDispatch} from "../../contexts/AggsContext";
import {SessionContext} from "../../contexts/SessionContext";
import {ViewSettingsContext} from "../../contexts/ViewSettingsContext";
import {useAssetGroupState} from "../../contexts/AssetGroupContext";
import {useFilters} from "../helpers/useFilters";
import {useParams} from "react-router-dom";
import api from "../api";
import {useAssetsDispatch, useAssetsState} from "../../contexts/AssetsContext";
import {useAssetsPageDispatch, useAssetsPageState} from "../../contexts/AssetsPageContext";
import {AppContext} from "../../contexts/AppContext";
import {useSelectedAssetsDispatch, useSelectedAssetsState} from "../../contexts/SelectedAssetsContext";
import {notification} from "antd";
import useCurrentOrg from "../helpers/useCurrentOrg";
import useRequestRefreshScheduler from "../helpers/useRequestRefreshScheduler";
import {loadAssetRefreshWindow} from './loadAssetRefreshWindow';

export default ({})=>{
    // Grand Central for loading and new assets

    const {page} = useAssetsPageState()
    const assetsPageDispatch = useAssetsPageDispatch()

    // Only dispatch Aggs on initial Assets load:

    const {reloads} = useLoadedAssetsState()

    const {state} = useContext(SessionContext);
    const {taxonomyFilterModes, assetSortOrder, currentOrg} = state;
    const {viewMode} = useContext(ViewSettingsContext);

    const {
        currentAssetGroup,
        currentWorkflowStep,
        currentUpload,
        viewAllType,
        currentUploadType,
        currentContribution,
        myCurrentContribution,
        currentTag,
        currentFederationOrg,
    } = useAssetGroupState()

    const {location} = useFilters()
    // Map display changes reuse the loaded pins, clusters, and density points.
    const assetSearchParams = new URLSearchParams(location.search)
    assetSearchParams.delete('map_display')
    const assetSearch = assetSearchParams.toString()

    // Prevent initial non-asset-group API call, before we have Group details
    const {slug, type, uploadGuid, contributionId, myContributionId, uploadType, shareCode, accessRequestId, tagId, folder, organizationSlug} = useParams();

    const {assetLoader} = useAssetLoader()
    const aggsDispatch = useAggsDispatch()
    useEffect(() => () => {
        latestAssetLoad += 1;
        aggsDispatch({type: 'searchStopped'});
    }, [aggsDispatch]);
    const liveRefreshScheduler = useRequestRefreshScheduler(
        () => assetLoader({page: 1, rejectOnError: true, background: true}),
        {delay: 250, debounce: false, onError: error => console.warn('Asset refresh failed', error)}
    )

    // Set Page 1 for new searches / containers / etc:
    // Note: this will fire twice for Groups w/ Workflows, because the direct group descendents are being returned

    let reloading;

    useEffect(()=>{
        if(page != 1) {
            reloading = true
            assetsPageDispatch({type:'reload'})
        }
    }, [currentOrg?.id, assetSearch, taxonomyFilterModes,
        currentAssetGroup?.id, currentAssetGroup?.folder?.id, currentWorkflowStep?.id, viewAllType, viewMode, assetSortOrder,
        currentUploadType, currentContribution?.id, myCurrentContribution?.id, currentTag?.id])

    useEffect(()=> {
        if(reloads > 0) {
            if(page != 1) assetsPageDispatch({type:'reload'})
            liveRefreshScheduler.schedule()
        }
    }, [reloads])

    useEffect(() => {
        const onRefresh = event => {
            const scopes = event.detail?.scopes || []
            if (!scopes.includes('assets') && !scopes.includes('aggregations')) return

            if(page != 1) assetsPageDispatch({type:'reload'})
            const refresh = liveRefreshScheduler.schedulePromise()
            event.detail?.waitUntil?.(refresh, ['assets', 'aggregations'])
        }
        window.addEventListener('mediagraph:dataRefresh', onRefresh)
        return () => window.removeEventListener('mediagraph:dataRefresh', onRefresh)
    }, [page, liveRefreshScheduler])

    useEffect(() => {
        // Prevent previous page from loading when changing browse params:
        if(reloading && page != 1) return

        if(currentAssetGroup && !slug && !shareCode && !accessRequestId) return;
        if(slug && type && !currentAssetGroup && !viewAllType && !currentUploadType) return;
        if(!viewAllType && !currentUploadType && slug && slug != currentAssetGroup?.slug) return;
        if(uploadGuid && !currentUpload) return;
        if(contributionId && !currentContribution) return
        if(myContributionId && !myCurrentContribution) return
        if(uploadType && !currentUploadType) return
        if(shareCode && !currentAssetGroup) return;
        if(tagId && !currentTag) return;

        assetLoader()

    }, [currentOrg?.id, assetSearch, taxonomyFilterModes, assetSortOrder?.sort, assetSortOrder?.direction,
        currentAssetGroup?.id, currentAssetGroup?.folder?.id, currentWorkflowStep?.id, currentUpload?.id, viewAllType,
        currentUploadType, currentContribution?.id, myCurrentContribution?.id, currentTag?.id, viewMode === 'map', currentFederationOrg?.id]);

    return <></>
}

const assetsPerPage = 25

let latestAssetLoad = 0
let snapshotTimestamp = null

const useAssetLoader = ()=> {
    const {
        currentAssetGroup,
        currentWorkflowStep,
        currentUpload,
        viewAllType,
        currentUploadType,
        currentContribution,
        myCurrentContribution,
        currentTag,
        currentFederationOrg,
    } = useAssetGroupState()

    const {state} = useContext(SessionContext);
    const {taxonomyFilterModes, assetSortOrder} = state;
    const {viewMode} = useContext(ViewSettingsContext);
    const currentOrg = useCurrentOrg()

    const loadedAssetsDispatch = useLoadedAssetsDispatch();
    const {assetIds: loadedAssetIds} = useLoadedAssetsState();
    const assetsDispatch = useAssetsDispatch();
    const aggsDispatch = useAggsDispatch()
    const {filters: urlFilters} = useFilters()
    const {map_display: mapDisplay, ...filters} = urlFilters

    const selectedAssetsDispatch = useSelectedAssetsDispatch()

    const assetLoader = ({page= 1, cb, rejectOnError = false, background = false}={})=> {
        console.log('loading page:', page, currentUploadType)

        if(page === 1) {
            if(!background) loadedAssetsDispatch({type: 'assetsLoading'});
            snapshotTimestamp = Date.now()
        }

        // HACK: prevent re-renders when selecting assets
        const selectedAssetIds = JSON.parse(sessionStorage.getItem(`selected-assets-reducer-${currentOrg?.id}`))?.selectedAssetIds

        // Reconcile the loaded window atomically. Keep its cards mounted while
        // the request runs, including later pages in the virtualized grid.
        const refreshSize = background ? Math.max(assetsPerPage, loadedAssetIds.findLastIndex(id => id != null) + 1) : assetsPerPage;
        let params = {
            page,
            include_totals: false,
            include_selection: page === 1,
            per_page: assetsPerPage,
            asset_group_id: currentAssetGroup?.id,
            workflow_step_id: currentWorkflowStep?.asset_group_id == currentAssetGroup?.asset_group_id ? currentWorkflowStep?.id : null,
            upload_id: currentUpload?.id,
            taxonomy_filter_modes: taxonomyFilterModes,
            view_all: viewAllType,
            upload_type: currentUploadType,
            my_contribution_id: myCurrentContribution?.id,
            contribution_id: currentContribution?.id,
            sort: assetSortOrder?.sort,
            direction: assetSortOrder?.direction,
            gps: viewMode == 'map',
            map_clusters: viewMode == 'map',
            map_density: viewMode == 'map',
            selected_asset_ids: selectedAssetIds,
            taxonomy_tag_id: currentTag?.id,
            snapshot_timestamp: snapshotTimestamp,
            ...filters,
        };

        if(currentFederationOrg?.id) params.organization_id = currentFederationOrg.id;

        if(currentAssetGroup) {
            const field = {
                'StorageFolder': 'storage_folder_id',
                'Collection': 'collection_id',
                'Lightbox': 'lightbox_id'
            }[currentAssetGroup.type]

            params[field] = currentAssetGroup.folder?.id || currentAssetGroup.id;
            params.asset_group_organization_id = currentAssetGroup.organization_id

            if(['access_request', 'access_grant'].includes(currentAssetGroup.sub_type)) params.access_request_id = currentAssetGroup.access_request_id

            if(currentAssetGroup.folder) params.lightbox_folder_id = currentAssetGroup.folder.id
        }

        if(page === 1) latestAssetLoad += 1;
        const assetLoadNumber = latestAssetLoad;

        if(page === 1) {
            window.currentSearchParams = params
            aggsDispatch({type: 'searchStarted', searchId: assetLoadNumber, params, background, organizationId: currentOrg?.id});
        }

        const request = parameters => api.post('/api/assets/search', parameters);
        return (background ? loadAssetRefreshWindow(request, params, refreshSize) : request(params)).then(res => {
            // Prevent old loads from clobbering new ones:
            if(assetLoadNumber != latestAssetLoad) return cb && cb();

            if(res.data.error) {
                notification.error({message:'Search Error', description: res.data.error, duration: 0})
                if(!background) loadedAssetsDispatch({type: 'assetsLoaded', assets: [], page, per_page: assetsPerPage, total: 0});
                cb && cb(0)
                if (rejectOnError) throw new Error(String(res.data.error))
                return
            }

            if(res.data.assets) {
                assetsDispatch({type: 'assetsLoaded', assets: res.data.assets})
                loadedAssetsDispatch({
                    type: background ? 'assetsRefreshed' : 'assetsLoaded',
                    assets: res.data.assets,
                    page,
                    per_page: refreshSize,
                    total: res.data.total_entries
                });
            }

            const total = res.data.total_display ?? res.data.total_entries

            let facetsDone;
            if(page === 1) {
                let onSettled;
                if(background) facetsDone = new Promise(resolve => { onSettled = resolve });
                aggsDispatch({
                    type: 'searchLoaded',
                    searchId: assetLoadNumber,
                    coords: res.data.coords,
                    mapData: res.data.map,
                    total,
                    onSettled,
                })
            }

            if(page == 1 && res.data.ids_in_selection) {
                const ids = filters.selection === 'unselected' ? selectedAssetIds : res.data.ids_in_selection
                if(background) {
                    // Cards stay interactive during live refreshes; preserve
                    // selection changes made after this request started.
                    selectedAssetsDispatch({type: 'reconcileSelection', requestedIds: selectedAssetIds || [], ids: ids || []})
                } else {
                    selectedAssetsDispatch({type: 'setSelection', ids})
                }
            }

            cb && cb(total)
            return facetsDone?.then(result => {
                if(result.failed && rejectOnError) throw new Error('Facet refresh failed');
            });
        }).catch(error => {
            if(background && assetLoadNumber === latestAssetLoad) loadedAssetsDispatch({type: 'assetsRefreshFailed'});
            throw error;
        })
    }

    const aggsLoader = ({page, cb}={})=>{
        aggsDispatch({type: 'refreshFacets'});
        cb?.();
    }

    return {assetLoader, assetsPerPage, aggsLoader}
}

export {useAssetLoader}
